<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import ServiceCard from '../components/ServiceCard.vue';
import Header from '../components/Header.vue';
import Footer from '../components/Footer.vue';
import { loadPolygonMaskPlugin } from '@tsparticles/plugin-polygon-mask';
import { loadFull } from 'tsparticles';

import electrical1 from '@/assets/images/electrical-1.jpg';
import electrical2 from '@/assets/images/electrical-2.jpg';
import electrical3 from '@/assets/images/electrical-3.jpg';
import electrical4 from '@/assets/images/electrical-6.jpg';
import electrical5 from '@/assets/images/electrical-5.jpg';
import electrical6 from '@/assets/images/electrical-4.jpg';
import companyBuilding from '@/assets/images/company-building.jpg';

import ElectricomLogo from '@/assets/partners/Electricom-New-Logo.png';
import APCLogo from '@/assets/partners/APC.png';
import SchneiderLogo from '@/assets/partners/schneider.png';
import MitsumiLogo from '@/assets/partners/mitsumi.png';
import ASLLogo from '@/assets/partners/asl.png';
import ThamesLogo from '@/assets/partners/thames.jpg';
import VertivLogo from '@/assets/partners/vertiv.png';
import MetsecLogo from '@/assets/partners/metsec.png';

const isLoading = ref(true);

let preloaderTimeout = null;

const hidePreloader = () => {
  clearTimeout(preloaderTimeout);
  isLoading.value = false;
  document.body.style.overflow = 'auto';
};

preloaderTimeout = setTimeout(hidePreloader, 1500);

window.addEventListener('load', hidePreloader);

onUnmounted(() => {
  clearTimeout(preloaderTimeout);
  window.removeEventListener('load', hidePreloader);
});

const slideLoadingStates = ref([]);
const isTransitioning = ref(false);

const slides = ref([
  {
    image: electrical1,
    alt: 'Electrical Installation 1',
    title: 'Advanced Electrical Solutions',
    description: 'Providing state-of-the-art electrical installations for commercial and residential needs.',
    cta: 'Request a Quote',
    ctaLink: '/contact',
  },
  {
    image: electrical2,
    alt: 'Electrical Installation 2',
    title: 'Efficient Power Systems',
    description: 'Delivering reliable and energy-efficient power solutions for all industries.',
    cta: 'Explore Services',
    ctaLink: '/services',
  },
  {
    image: electrical3,
    alt: 'Power Backup Systems',
    title: 'Uninterrupted Power Supply',
    description: 'Robust backup systems to ensure your operations never stop.',
    cta: 'Learn More',
    ctaLink: '/services/power',
  },
  {
    image: electrical4,
    alt: 'Fire Safety Systems',
    title: 'Cutting-Edge Fire Protection',
    description: 'Advanced fire detection and suppression for maximum safety.',
    cta: 'Get a Free Consultation',
    ctaLink: '/contact',
  },
  {
    image: electrical5,
    alt: 'Automation Systems',
    title: 'Smart Automation Solutions',
    description: 'Integrate IoT for seamless control of lighting, security, and energy.',
    cta: 'Discover Automation',
    ctaLink: '/services/automation',
  },
  {
    image: electrical6,
    alt: 'Data Center Power',
    title: 'Mission-Critical Power Systems',
    description: 'Specialized power solutions for data centers and telecom.',
    cta: 'Contact Us',
    ctaLink: '/contact',
  },
]);

const currentSlide = ref(0);
const transitionType = ref('fade');
const slideProgress = ref(0);
let slideInterval = null;
let progressInterval = null;

const transitionTypes = ['fade', 'particle'];

const preloadImages = () => {
  slideLoadingStates.value = slides.value.map(() => false);

  slides.value.forEach((slide, index) => {
    const img = new Image();
    img.onload = () => {
      slideLoadingStates.value[index] = true;
    };
    img.onerror = () => {
      slideLoadingStates.value[index] = false;
    };
    img.src = slide.image;
  });
};

const isCurrentSlideLoaded = computed(() => {
  return slideLoadingStates.value[currentSlide.value] === true;
});

const nextSlide = () => {
  if (isTransitioning.value) return;

  isTransitioning.value = true;
  const nextIndex = (currentSlide.value + 1) % slides.value.length;

  transitionType.value = transitionTypes[Math.floor(Math.random() * transitionTypes.length)];

  currentSlide.value = nextIndex;
  slideProgress.value = 0;

  setTimeout(() => {
    isTransitioning.value = false;
  }, 1200);
};

const goToSlide = (index) => {
  if (isTransitioning.value || index === currentSlide.value) return;

  isTransitioning.value = true;
  transitionType.value = transitionTypes[Math.floor(Math.random() * transitionTypes.length)];
  currentSlide.value = index;
  slideProgress.value = 0;

  if (slideInterval) {
    clearInterval(slideInterval);
    slideInterval = setInterval(nextSlide, 10000);
  }

  if (progressInterval) {
    clearInterval(progressInterval);
    startProgressTimer();
  }

  setTimeout(() => {
    isTransitioning.value = false;
  }, 1200);
};

const handleImageLoad = () => {
  slideLoadingStates.value[currentSlide.value] = true;
};

const handleImageError = () => {
  slideLoadingStates.value[currentSlide.value] = false;
};

const startProgressTimer = () => {
  slideProgress.value = 0;
  progressInterval = setInterval(() => {
    slideProgress.value += 1;
    if (slideProgress.value >= 100) {
      clearInterval(progressInterval);
    }
  }, 100);
};

const particlesOptions = ref({
  background: {
    color: { value: '#1e3a8a' },
  },
  fpsLimit: 60,
  interactivity: {
    events: {
      onClick: { enable: false },
      onHover: { enable: false },
      resize: true,
    },
  },
  particles: {
    color: {
      value: ['#ffffff', '#fbbf24', '#3b82f6'],
    },
    links: {
      color: '#ffffff',
      distance: 100,
      enable: true,
      opacity: 0.3,
      width: 1,
    },
    move: {
      direction: 'none',
      enable: true,
      outModes: { default: 'bounce' },
      random: true,
      speed: { min: 1, max: 3 },
      straight: false,
    },
    number: {
      density: { enable: true, area: 1000 },
      value: 50,
    },
    opacity: {
      value: { min: 0.3, max: 0.8 },
      animation: {
        enable: true,
        speed: 1,
        minimumValue: 0.1,
        sync: false,
      },
    },
    shape: {
      type: ['circle'],
    },
    size: {
      value: { min: 1, max: 4 },
      animation: {
        enable: true,
        speed: 2,
        minimumValue: 1,
        sync: false,
      },
    },
  },
  detectRetina: true,
});

let particlesContainer = null;

const particlesInit = async (engine) => {
  try {
    await loadFull(engine);
    await loadPolygonMaskPlugin(engine);
  } catch (error) {
    transitionType.value = 'fade';
  }
};

const particlesLoaded = async (container) => {
  particlesContainer = container;
};

const onBeforeEnter = () => {
  if (particlesContainer && transitionType.value === 'particle') {
    try {
      particlesContainer.options.particles.move.speed = { min: 8, max: 15 };
      particlesContainer.options.particles.number.value = 200;
      particlesContainer.options.particles.opacity.value = { min: 0.1, max: 0.9 };
      particlesContainer.options.particles.size.value = { min: 1, max: 8 };
      particlesContainer.options.particles.links.enable = false;
      particlesContainer.refresh();
    } catch (error) {
      transitionType.value = 'fade';
    }
  }
};

const onAfterEnter = () => {
  if (particlesContainer && transitionType.value === 'particle') {
    try {
      setTimeout(() => {
        particlesContainer.options.particles.move.speed = { min: 1, max: 3 };
        particlesContainer.options.particles.number.value = 50;
        particlesContainer.options.particles.opacity.value = { min: 0.3, max: 0.8 };
        particlesContainer.options.particles.size.value = { min: 1, max: 4 };
        particlesContainer.options.particles.links.enable = true;
        particlesContainer.refresh();
      }, 1000);
    } catch (error) {
      transitionType.value = 'fade';
    }
  }
};

const services = [
  {
    title: 'General Electrical Installations',
    description: 'Design, installation, maintenance, and repair of electrical equipment for all sectors.',
    link: '/services/electrical',
  },
  {
    title: 'Power Generation & Backup',
    description: 'Standby generators, UPS systems, voltage regulators, and surge protection.',
    link: '/services/power',
  },
  {
    title: 'Data Center Power Systems',
    description: 'Critical power solutions for telecom, banking, and data centers.',
    link: '/services/datacenter',
  },
  {
    title: 'Home & Commercial Automation',
    description: 'IoT solutions for lighting, climate control, security, and energy management.',
    link: '/services/automation',
  },
  {
    title: 'Fire Safety & Protection',
    description: 'Fire detection, alarm systems, suppression systems, and compliance services.',
    link: '/services/fire-safety',
  },
];

const whyChooseUs = [
  {
    title: 'Experience',
    description:
      'In the industry, we have what it takes to build systems that will exceed your expectations. Our staff are well trained by the OEMs and boast of first-hand experience in the technical front.',
    iconPath:
      'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  },
  {
    title: 'Products and Partners',
    description:
      'With industry-leading products, which we choose for their durability and outstanding performance, you can be sure that our systems will last for many decades. Thus, value-addition and peace of mind allowing you to focus on your core business.',
    iconPath:
      'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  },
  {
    title: 'Expertise',
    description:
      'We handle the full site analysis, system engineering, permitting, installation, commissioning, monitoring and maintenance of your systems.',
    iconPath:
      'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  },
];

const partners = ref([
  { name: 'Electricom', logo: ElectricomLogo, alt: 'Electricom Logo' },
  { name: 'APC', logo: APCLogo, alt: 'APC Logo' },
  { name: 'Schneider Electric', logo: SchneiderLogo, alt: 'Schneider Electric Logo' },
  { name: 'Mitsumi', logo: MitsumiLogo, alt: 'Mitsumi Logo' },
  { name: 'ASL', logo: ASLLogo, alt: 'ASL Logo' },
  { name: 'Thames', logo: ThamesLogo, alt: 'Thames Logo' },
  { name: 'Vertiv', logo: VertivLogo, alt: 'Vertiv Logo' },
  { name: 'Metsec', logo: MetsecLogo, alt: 'Metsec Logo' },
]);

const currentPartnerIndex = ref(0);
const carouselInterval = ref(null);
const logosPerView = ref(5);
const isCarouselTransitioning = ref(false);

const nextPartners = () => {
  if (isCarouselTransitioning.value) return;

  isCarouselTransitioning.value = true;
  currentPartnerIndex.value = (currentPartnerIndex.value + 1) % partners.value.length;

  setTimeout(() => {
    isCarouselTransitioning.value = false;
  }, 600);
};

const prevPartners = () => {
  if (isCarouselTransitioning.value) return;

  isCarouselTransitioning.value = true;
  currentPartnerIndex.value =
    (currentPartnerIndex.value - 1 + partners.value.length) % partners.value.length;

  setTimeout(() => {
    isCarouselTransitioning.value = false;
  }, 600);
};

const extendedPartners = computed(() => {
  return [...partners.value, ...partners.value, ...partners.value];
});

const calculateTransform = computed(() => {
  const itemWidth = 100 / logosPerView.value;
  return -(currentPartnerIndex.value * itemWidth);
});

onMounted(() => {
  preloadImages();
  setTimeout(() => {
    slideInterval = setInterval(nextSlide, 10000);
    startProgressTimer();
  }, 2000);

  carouselInterval.value = setInterval(nextPartners, 4000);
});

onUnmounted(() => {
  if (slideInterval) {
    clearInterval(slideInterval);
  }
  if (progressInterval) {
    clearInterval(progressInterval);
  }
  if (carouselInterval.value) {
    clearInterval(carouselInterval.value);
  }
  if (particlesContainer) {
    particlesContainer.destroy();
    particlesContainer = null;
  }
});
</script>

<template>
  <div v-if="isLoading" class="fixed inset-0 z-50 bg-blue-900 flex flex-col items-center justify-center">
    <div class="relative w-28 h-28 mb-8">
      <div class="absolute inset-0 border-2 border-yellow-500/30 rounded-full"></div>
      <div class="absolute inset-0 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      <img src="../assets/images/logo.png" alt="Trevia Logo" class="w-full h-full object-contain p-4" />
    </div>
    <div class="text-white text-sm tracking-widest uppercase font-aptos">Loading</div>
  </div>

  <div class="min-h-screen bg-gray-50 relative overflow-hidden font-aptos">
    <Header />

    <div class="relative w-full h-screen max-h-[100vh] overflow-hidden bg-blue-900">
      <div class="absolute top-0 left-0 right-0 h-1 bg-white/20 z-40">
        <div
          class="h-full bg-yellow-500 transition-all duration-100 ease-out"
          :style="{ width: slideProgress + '%' }"
        ></div>
      </div>

      <vue-particles
        id="tsparticles"
        :particlesInit="particlesInit"
        :particlesLoaded="particlesLoaded"
        :options="particlesOptions"
        class="absolute inset-0 z-10"
        :class="{ 'opacity-50': transitionType === 'particle' }"
      />

      <div class="relative w-full h-full z-20">
        <transition
          name="fade"
          mode="out-in"
          @before-enter="onBeforeEnter"
          @after-enter="onAfterEnter"
        >
          <div :key="currentSlide" class="absolute inset-0 flex items-center justify-center text-center px-4">
            <div v-if="!isCurrentSlideLoaded" class="absolute inset-0 bg-blue-900 flex items-center justify-center">
              <div class="text-white text-sm tracking-widest uppercase font-aptos">Loading</div>
            </div>

            <img
              :src="slides[currentSlide].image"
              :alt="slides[currentSlide].alt"
              class="w-full h-full absolute inset-0 transition-opacity duration-1000"
              :class="[
                { 'opacity-100': isCurrentSlideLoaded, 'opacity-0': !isCurrentSlideLoaded },
                currentSlide === 0 || currentSlide === 1
                  ? 'object-cover scale-110 sm:scale-125 md:scale-110 lg:scale-105 object-center sm:object-[center_30%] md:object-center'
                  : 'object-cover'
              ]"
              @load="handleImageLoad"
              @error="handleImageError"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-blue-950/90 via-blue-950/50 to-blue-950/30"></div>

            <div
              class="relative z-10 text-white max-w-4xl px-8 transition-all duration-1000"
              :class="[
                'animate__animated',
                isCurrentSlideLoaded
                  ? currentSlide % 2 === 0
                    ? 'animate__fadeInLeft'
                    : 'animate__fadeInRight'
                  : 'opacity-0',
              ]"
              :style="{ animationDelay: isCurrentSlideLoaded ? '0.5s' : '0s' }"
            >
              <span
                class="inline-block text-yellow-500 text-xs tracking-[0.3em] uppercase font-semibold mb-4 animate__animated transition-all duration-1000 font-aptos"
                :class="isCurrentSlideLoaded ? 'animate__fadeInDown opacity-100' : 'opacity-0'"
                :style="{ animationDelay: isCurrentSlideLoaded ? '0.6s' : '0s' }"
              >
                Trevia Systems & Services
              </span>
              <h1
                class="text-4xl md:text-6xl font-bold mb-6 leading-tight animate__animated transition-all duration-1000 font-aptos"
                :class="isCurrentSlideLoaded ? 'animate__fadeInDown opacity-100' : 'opacity-0'"
                :style="{ animationDelay: isCurrentSlideLoaded ? '0.8s' : '0s' }"
              >
                {{ slides[currentSlide].title }}
              </h1>
              <p
                class="text-lg md:text-2xl mb-8 text-white/85 max-w-2xl mx-auto animate__animated transition-all duration-1000 font-aptos"
                :class="isCurrentSlideLoaded ? 'animate__fadeInUp opacity-100' : 'opacity-0'"
                :style="{ animationDelay: isCurrentSlideLoaded ? '1.2s' : '0s' }"
              >
                {{ slides[currentSlide].description }}
              </p>
              <router-link
                :to="slides[currentSlide].ctaLink"
                class="bg-yellow-500 text-blue-900 px-8 py-3.5 rounded-full font-bold hover:bg-yellow-400 transition duration-300 inline-block animate__animated font-aptos shadow-lg shadow-yellow-500/20"
                :class="isCurrentSlideLoaded ? 'animate__bounceIn animate__delay-2s opacity-100' : 'opacity-0'"
              >
                {{ slides[currentSlide].cta }}
              </router-link>
            </div>
          </div>
        </transition>

        <div class="absolute bottom-8 left-0 right-0 flex justify-center space-x-2 z-30">
          <button
            v-for="(slide, index) in slides"
            :key="index"
            @click="goToSlide(index)"
            class="h-1.5 rounded-full transition-all duration-300"
            :class="currentSlide === index ? 'bg-yellow-500 w-8' : 'bg-white/40 w-1.5 hover:bg-white/60'"
            :disabled="isTransitioning"
            :aria-label="`Go to slide ${index + 1}`"
          ></button>
        </div>
      </div>
    </div>

    <section class="py-24 bg-white w-full">
      <div class="container mx-auto px-6">
        <div class="grid md:grid-cols-2 gap-16 items-center">
          <div class="space-y-6">
            <span class="text-yellow-600 text-xs tracking-[0.25em] uppercase font-bold font-aptos">About Us</span>
            <h2 class="text-4xl font-bold text-blue-900 leading-tight font-aptos">About Trevia Systems</h2>
            <p class="text-lg leading-relaxed text-gray-600 font-aptos">
              Founded in 2017 and headquartered in Nairobi, Trevia Systems & Services Limited is a
              highly diversified and integrated company providing specialized solutions in Electrical
              Installations, Mission-critical Power Systems, Building Automation, and Fire Safety.
            </p>
            <p class="text-lg leading-relaxed text-gray-600 font-aptos">
              We have built a solid reputation for reliability, technical precision, and timely
              delivery across diverse sectors. At Trevia Systems & Services Limited, professionalism
              and collaboration underpin our business.
            </p>
            <div class="flex flex-wrap gap-4 pt-2">
              <router-link
                to="/about"
                class="bg-blue-900 text-white px-8 py-3 rounded-full font-bold hover:bg-blue-800 transition duration-300 font-aptos"
              >
                Learn More
              </router-link>
              <router-link
                to="/contact"
                class="border-2 border-blue-900 text-blue-900 px-8 py-3 rounded-full font-bold hover:bg-blue-900 hover:text-white transition duration-300 font-aptos"
              >
                Contact Us
              </router-link>
            </div>
          </div>
          <div class="relative h-96 rounded-3xl overflow-hidden shadow-2xl group">
            <img
              :src="companyBuilding"
              alt="Trevia Office"
              class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-blue-950/40 to-transparent"></div>
          </div>
        </div>
      </div>
    </section>

    <section class="py-24 bg-gray-50 w-full">
      <div class="container mx-auto px-6">
        <div class="text-center mb-16">
          <span class="text-yellow-600 text-xs tracking-[0.25em] uppercase font-bold font-aptos">What We Do</span>
          <h2 class="text-4xl font-bold text-blue-900 mt-3 mb-4 font-aptos">Our Services</h2>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto font-aptos">
            Comprehensive solutions tailored to meet your specific needs across various sectors.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <ServiceCard
            v-for="(service, index) in services"
            :key="index"
            :title="service.title"
            :description="service.description"
            :link="service.link"
            :index="index"
            class="transform hover:-translate-y-1.5 transition-transform duration-300"
          />
        </div>
      </div>
    </section>

    <section class="py-24 bg-blue-900 text-white w-full relative overflow-hidden">
      <div class="container mx-auto px-6 relative z-10">
        <div class="text-center mb-16">
          <span class="text-yellow-500 text-xs tracking-[0.25em] uppercase font-bold font-aptos">Our Edge</span>
          <h2 class="text-4xl font-bold mt-3 mb-4 font-aptos">Why Choose Trevia?</h2>
          <p class="text-lg text-white/70 max-w-2xl mx-auto font-aptos">
            We combine technical expertise with industry-leading products to deliver exceptional results.
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-8">
          <div
            v-for="(reason, index) in whyChooseUs"
            :key="index"
            class="bg-white/5 p-8 rounded-2xl backdrop-blur-sm border border-white/10 hover:border-yellow-500/60 hover:bg-white/[0.07] transition-all duration-300"
          >
            <div class="bg-white/10 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
              <svg class="w-7 h-7 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="reason.iconPath"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold mb-3 font-aptos">{{ reason.title }}</h3>
            <p class="text-white/70 leading-relaxed font-aptos">{{ reason.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="py-24 bg-white w-full">
      <div class="container mx-auto px-6">
        <div class="text-center mb-16">
          <span class="text-yellow-600 text-xs tracking-[0.25em] uppercase font-bold font-aptos">Partners</span>
          <h2 class="text-4xl font-bold text-blue-900 mt-3 mb-4 font-aptos">Our Trusted Partners</h2>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto font-aptos">
            We partner with industry-leading manufacturers to deliver world-class solutions.
          </p>
        </div>

        <div class="relative max-w-6xl mx-auto">
          <div class="overflow-hidden px-12">
            <div
              class="flex transition-transform duration-[600ms] ease-in-out"
              :style="{ transform: `translateX(${calculateTransform}%)` }"
            >
              <div
                v-for="(partner, index) in extendedPartners"
                :key="`${partner.name}-${index}`"
                class="flex-shrink-0 w-48 h-24 flex items-center justify-center p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-yellow-500/50 hover:shadow-md transition-all duration-300 mx-2"
                :style="{ minWidth: `${100 / logosPerView}%` }"
              >
                <img
                  :src="partner.logo"
                  :alt="partner.alt"
                  class="max-h-16 max-w-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  :title="partner.name"
                  loading="lazy"
                  @error="$event.target.style.display = 'none'"
                />
              </div>
            </div>
          </div>

          <button
            @click="prevPartners"
            :disabled="isCarouselTransitioning"
            class="absolute left-0 top-1/2 -translate-y-1/2 bg-blue-900 text-white p-3 rounded-full hover:bg-blue-800 transition-colors duration-300 z-10 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous partners"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          <button
            @click="nextPartners"
            :disabled="isCarouselTransitioning"
            class="absolute right-0 top-1/2 -translate-y-1/2 bg-blue-900 text-white p-3 rounded-full hover:bg-blue-800 transition-colors duration-300 z-10 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next partners"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>

          <div class="flex justify-center mt-8 space-x-2">
            <button
              v-for="(partner, index) in partners"
              :key="index"
              @click="currentPartnerIndex = index"
              class="h-1.5 rounded-full transition-all duration-300"
              :class="currentPartnerIndex === index ? 'bg-blue-900 w-6' : 'bg-gray-300 w-1.5'"
              :aria-label="`Go to ${partner.name}`"
            ></button>
          </div>
        </div>
      </div>
    </section>

    <section class="py-20 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
      <div class="container mx-auto px-6 text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-5 font-aptos">Ready to Power Your Project?</h2>
        <p class="text-lg mb-8 max-w-2xl mx-auto text-white/85 font-aptos">
          Contact us today to discuss your electrical and power needs with our expert team.
        </p>
        <router-link
          to="/contact"
          class="bg-yellow-500 text-blue-900 px-8 py-4 rounded-full font-bold hover:bg-yellow-400 transition duration-300 inline-block font-aptos shadow-lg shadow-yellow-500/20"
        >
          Get in Touch
        </router-link>
      </div>
    </section>

    <Footer />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Aptos:wght@300;400;500;600;700&display=swap');

.font-aptos {
  font-family: 'Aptos', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

h1, h2, h3, h4, h5, h6 {
  font-family: 'Aptos', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  letter-spacing: -0.02em;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

#tsparticles {
  pointer-events: none;
}

.animate__animated {
  animation-duration: 1s;
  animation-fill-mode: both;
}

.animate__delay-2s {
  animation-delay: 2s;
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

button:focus-visible,
a:focus-visible {
  outline: 2px solid #eab308;
  outline-offset: 2px;
  border-radius: 4px;
}

@media (max-width: 640px) {
  .slider-image-zoom {
    transform: scale(1.25);
    transform-origin: center 30%;
  }
}

@media (min-width: 641px) and (max-width: 768px) {
  .slider-image-zoom {
    transform: scale(1.15);
  }
}

@media (min-width: 769px) {
  .slider-image-zoom {
    transform: scale(1.05);
  }
}
</style>